import React, { useState } from 'react';
import { Button, TextField, CircularProgress, Alert, Box, Grid, Typography } from '@mui/material';
import { auth } from './firebase';  // Importer l'authentification depuis firebase.js
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';  // Importer les fonctions nécessaires
import GoogleIcon from '@mui/icons-material/Google'; // Icône Google

const AuthForm = ({ onAuthSuccess, setUser }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [errorState, setErrorState] = useState('');
  const [loading, setLoading] = useState(false);
  const [isNewUser, setIsNewUser] = useState(true); // Contrôler l'état de l'utilisateur (inscription ou connexion)

  // Fonction d'authentification
  const handleAuth = async () => {
    setLoading(true);
    setErrorState('');
    try {
      let user;
      if (isNewUser) {
        // Inscription
        if (!firstName || !lastName || !password) {
          setErrorState('Veuillez remplir tous les champs avant de continuer.');
          return;
        }

        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        user = userCredential.user;
        await user.updateProfile({ displayName: `${firstName} ${lastName}` });
      } else {
        // Connexion
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        user = userCredential.user;

        // Vérification de l'utilisateur
        if (!user) {
          setErrorState('Échec de la connexion. Veuillez vérifier vos identifiants.');
          setLoading(false);
          return;
        }
      }

      // Mise à jour de l'utilisateur dans l'état global
      setUser(user);
      onAuthSuccess();  // Appel de la fonction pour la redirection ou actions après la connexion
    } catch (error) {
      setErrorState(isNewUser ? `Erreur d'inscription : ${error.message}` : `Erreur de connexion : ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Connexion avec Google
  const handleGoogleAuth = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Mise à jour de l'utilisateur dans l'état global
      setUser(user);
      onAuthSuccess();  // Appel de la fonction pour la redirection ou actions après la connexion
    } catch (error) {
      setErrorState(`Erreur de connexion Google : ${error.message}`);
    }
  };

  // Réinitialisation du mot de passe
  const handlePasswordReset = async () => {
    try {
      if (email) {
        await sendPasswordResetEmail(auth, email);
        setErrorState('Un email de réinitialisation a été envoyé.');
      } else {
        setErrorState('Veuillez entrer votre adresse email.');
      }
    } catch (error) {
      setErrorState(`Erreur lors de l'envoi de l'email de réinitialisation : ${error.message}`);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '20px' }}>
      <Grid container spacing={3} sx={{ maxWidth: '1200px', width: '100%' }}>
        {/* Partie gauche : Nom du service, description et slogan en haut */}
        <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', backgroundColor: '#4C96D7', padding: 3, borderRadius: 5, color: 'white', minHeight: 'auto' }}>
          <Typography variant="h3" sx={{ fontWeight: 'bold', marginBottom: 2 }}>Aladinn.AI</Typography>
          <Typography variant="body1" sx={{ marginBottom: 3 }}>
            Aladinn.AI est une plateforme révolutionnaire qui aide à développer les talents, gérer les compétences et transformer les défis éducatifs en opportunités.
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: 3 }}>Révèle ton Génie !</Typography>

          {/* Affichage dynamique du bouton "Je m'inscris" ou "Je me connecte" sur la page de gauche */}
          {isNewUser ? (
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2, borderRadius: 25 }}
              onClick={() => setIsNewUser(false)} // Passer à la page de connexion
            >
              Je me connecte
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              sx={{ mt: 2, borderRadius: 25 }}
              onClick={() => setIsNewUser(true)} // Passer à la page d'inscription
            >
              Je m'inscrits
            </Button>
          )}
        </Grid>

        {/* Partie droite : Formulaire d'authentification */}
        <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', backgroundColor: '#ffffff', padding: 3, borderRadius: 5, boxShadow: 3 }}>
          {errorState && <Alert severity="error">{errorState}</Alert>}
          
          {isNewUser && (
            <>
              <TextField
                label="Prénom"
                variant="outlined"
                fullWidth
                margin="normal"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                sx={{ borderRadius: 2 }}
              />
              <TextField
                label="Nom"
                variant="outlined"
                fullWidth
                margin="normal"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                sx={{ borderRadius: 2 }}
              />
            </>
          )}

          <TextField
            label="Adresse email"
            variant="outlined"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ borderRadius: 2 }}
          />
          <TextField
            label="Mot de passe"
            type="password"
            variant="outlined"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ borderRadius: 2 }}
          />

          {/* Affichage dynamique du bouton sur la partie droite */}
          {isNewUser ? (
            <Button variant="contained" color="secondary" fullWidth sx={{ mt: 2, borderRadius: 25 }} onClick={handleAuth}>
              S'inscrire
            </Button>
          ) : (
            <Button variant="contained" color="primary" fullWidth sx={{ mt: 2, borderRadius: 25 }} onClick={handleAuth}>
              Se connecter
            </Button>
          )}

          <Button variant="text" color="secondary" onClick={() => setIsNewUser(!isNewUser)} sx={{ mt: 2 }}>
            {isNewUser ? "Vous avez déjà un compte ? Se connecter" : "Vous êtes nouveau ? S'inscrire"}
          </Button>

          <Button variant="text" color="primary" onClick={handlePasswordReset} sx={{ mt: 2 }}>
            Mot de passe oublié ?
          </Button>

          {/* Connexion avec Google */}
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            sx={{ mt: 2, borderRadius: 25 }}
            onClick={handleGoogleAuth}
            startIcon={<GoogleIcon />}
          >
            Se connecter avec Google
          </Button>

          {loading && <CircularProgress sx={{ mt: 2 }} />}
        </Grid>
      </Grid>
    </Box>
  );
};

export default AuthForm;
